import type { CSSProperties } from "react";
import { Box, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../utils";
import classes from "../../pages/customer-billing/BillingDetails.module.css";

export type AgingBucketKey = "NOT_DUE" | "D1_30" | "D31_60" | "D61_90" | "D90_PLUS";

export type AgingDetails = {
  CURRENT?: { total: number; count: number };
  DAYS_1_30?: { total: number; count: number };
  DAYS_31_60?: { total: number; count: number };
  DAYS_61_90?: { total: number; count: number };
  DAYS_91_PLUS?: { total: number; count: number };
};

interface Props {
  aging: {
    bucketCurrent: number;
    bucket1to30: number;
    bucket31to60: number;
    bucket61to90: number;
    bucket91Plus: number;
    details?: AgingDetails;
  } | null;
  activeBucket: AgingBucketKey | null;
  onBucketChange: (bucket: AgingBucketKey | null) => void;
}

const BUCKETS: {
  key: AgingBucketKey;
  detailKey: keyof AgingDetails;
  amountKey: "bucketCurrent" | "bucket1to30" | "bucket31to60" | "bucket61to90" | "bucket91Plus";
  labelKey: string;
  color: string;
  bg: string;
}[] = [
  {
    key: "NOT_DUE",
    detailKey: "CURRENT",
    amountKey: "bucketCurrent",
    labelKey: "ar.agingCurrent",
    color: "var(--mantine-color-green-6)",
    bg: "var(--mantine-color-green-0)",
  },
  {
    key: "D1_30",
    detailKey: "DAYS_1_30",
    amountKey: "bucket1to30",
    labelKey: "ar.aging1to30",
    color: "var(--mantine-color-yellow-5)",
    bg: "var(--mantine-color-yellow-0)",
  },
  {
    key: "D31_60",
    detailKey: "DAYS_31_60",
    amountKey: "bucket31to60",
    labelKey: "ar.aging31to60",
    color: "var(--mantine-color-yellow-6)",
    bg: "var(--mantine-color-yellow-0)",
  },
  {
    key: "D61_90",
    detailKey: "DAYS_61_90",
    amountKey: "bucket61to90",
    labelKey: "ar.aging61to90",
    color: "var(--mantine-color-red-6)",
    bg: "var(--mantine-color-red-0)",
  },
  {
    key: "D90_PLUS",
    detailKey: "DAYS_91_PLUS",
    amountKey: "bucket91Plus",
    labelKey: "ar.aging91plus",
    color: "var(--mantine-color-red-9)",
    bg: "var(--mantine-color-red-0)",
  },
];

export function AgingChart({ aging, activeBucket, onBucketChange }: Props) {
  const { t } = useTranslation("billing");

  if (!aging) return null;

  const items = BUCKETS.map((b) => {
    const amount = Number(aging[b.amountKey] || 0);
    const count = Number(aging.details?.[b.detailKey]?.count ?? 0);
    return { ...b, amount, count };
  });

  const total = items.reduce((s, i) => s + i.amount, 0);
  if (total === 0) return null;

  return (
    <Box className={classes.agingCard}>
      <Box className={classes.agingHead}>
        <Text component="h2" className={classes.agingTitle}>
          {t("detail.agingSection")}
        </Text>
        <Text className={classes.agingHint}>{t("detail.agingHint")}</Text>
      </Box>

      <Box className={classes.agingBar} aria-hidden>
        {items.map((item) => {
          if (item.amount <= 0) return null;
          const pct = (item.amount / total) * 100;
          return (
            <Box
              key={item.key}
              className={classes.agingSeg}
              style={{ width: `${pct}%`, background: item.color }}
            />
          );
        })}
      </Box>

      <Box className={classes.agingTiles}>
        {items.map((item) => {
          const pct = total > 0 ? Math.round((item.amount / total) * 1000) / 10 : 0;
          const active = activeBucket === item.key;
          return (
            <button
              key={item.key}
              type="button"
              className={`${classes.agingTile} ${active ? classes.agingTileActive : ""}`}
              style={
                {
                  "--tile-accent": item.color,
                  "--tile-bg": item.bg,
                } as CSSProperties
              }
              onClick={() => onBucketChange(active ? null : item.key)}
            >
              <Box className={classes.agingTileLabel}>
                <Box className={classes.agingDot} style={{ background: item.color }} />
                {t(item.labelKey)}
              </Box>
              <Box className={classes.agingAmount}>
                {currencyFormat(item.amount, { withSymbol: false })}
              </Box>
              <Box className={classes.agingMeta}>
                {t("ar.bucketMeta", { count: item.count, pct })}
              </Box>
            </button>
          );
        })}
      </Box>
    </Box>
  );
}
